/**
 * 割り算内包 GTP（IDGTP）向け: マージ実効の葉への按比例配分など。
 * 「帳尻パウチ（BP）」の計算は BalancingPouchLogic — 役割を混同しないこと。
 */
import { Pouch, Group } from '../types';
import {
  findGroupInclusiveGtps,
  getInclusiveGtpMergedGroupSheetTotal,
  groupHasDivisionInclusiveGtp,
  isInclusiveGtpOfGroup,
  isInMuteGroup,
} from './GroupLogic';
import { getPouchContributionForTotals, getPouchEffectiveContribution } from './BalancingPouchLogic';

/**
 * ÷内包時: マージ実効をマージ対象葉へ按比例配分した値（シート TOTAL・凡例・フッター統計の按分と整合）。
 * 内包 GTP・条件外は getPouchContributionForTotals と同一。
 */
export const getPouchInclusiveDivisionDistributedValue = (
  pouch: Pouch,
  groups: Group[],
  allPouches: Pouch[]
): number => {
  const parents = groups.filter((g) => g.pouches?.includes(pouch.id));
  const divParents = parents.filter((g) => groupHasDivisionInclusiveGtp(g, allPouches));

  if (divParents.length !== 1) {
    return getPouchContributionForTotals(pouch, groups, allPouches);
  }

  const G = divParents[0];
  const gtps = findGroupInclusiveGtps(G, allPouches);
  const gtpIds = new Set(gtps.map((g) => g.id));

  if (isInclusiveGtpOfGroup(pouch.id, G, allPouches)) {
    return 0;
  }

  const mergeLeaves = allPouches.filter(
    (p) =>
      G.pouches?.includes(p.id) &&
      !p._isDeleting &&
      !gtpIds.has(p.id) &&
      !isInMuteGroup(p.id, groups)
  );

  if (!mergeLeaves.some((p) => p.id === pouch.id)) {
    return getPouchContributionForTotals(pouch, groups, allPouches);
  }

  const merged = getInclusiveGtpMergedGroupSheetTotal(G, allPouches, groups);
  if (merged === null) {
    return getPouchContributionForTotals(pouch, groups, allPouches);
  }

  let S = 0;
  for (const p of mergeLeaves) {
    S += getPouchEffectiveContribution(p, groups, allPouches, G.id);
  }
  S = Math.round(S * 1000000000) / 1000000000;

  const effP = getPouchEffectiveContribution(pouch, groups, allPouches, G.id);

  if (Math.abs(S) < 1e-12) {
    const n = mergeLeaves.length;
    if (n === 0) return 0;
    return Math.round((merged / n) * 1000000000) / 1000000000;
  }

  return Math.round(merged * (effP / S) * 1000000000) / 1000000000;
};
